import { getApiHeaders } from '../utils/apiHeaders';

const getAiBaseUrl = () => process.env.REACT_APP_AI_BACKEND_BASE_URL;

export type ExtractionStatus = 'ok' | 'needs_review' | 'failed';
export type WarningSeverity = 'error' | 'warning' | 'info';
export type ServiceBasis = 'PRIVATE' | 'SIC' | 'SHARING' | 'UNKNOWN';
export type ActivityMode = 'private' | 'shared' | 'only_ticket';

export type ExtractionWarning = {
  code: string;
  severity: WarningSeverity;
  message: string;
  path?: string;
};

export type QuotationItineraryBuilderSeed = {
  client: {
    name: string;
    phoneNumber: string;
    email: string;
  };
  tripInfo: {
    title: string;
    subtitle?: string;
    tripOverview?: string;
    places: Array<{
      label: string;
      value: string;
      nights: number;
    }>;
    startDate: string;
    travellers: {
      adults: number;
      children: number;
      infants: number;
    };
    inclusions: string[];
    totalPrice: number;
    transferType?: string;
    hotelCategory?: string;
  };
  itinerary: Array<{
    day: number;
    title: string;
    place: {
      label: string;
      value: string;
      nights: number;
    };
    flights: Array<{
      airline: string;
      flightNumber: string;
      from: string;
      to: string;
      departureTime: string;
      arrivalTime: string;
      duration: string;
      stops: number;
      price: number;
      class?: string;
      provider?: string;
      fromAirportName?: string;
      toAirportName?: string;
    }>;
    activities: Array<{
      id?: string;
      slug: string;
      name: string;
      description: string;
      duration_minutes?: number;
      start_time?: string;
      remarks?: string;
      picture_url?: string;
      city?: string;
      category?: string;
      currency: string;
      is_active: boolean;
      variant_count?: number;
      variants?: Array<{
        mode: ActivityMode;
        is_active: boolean;
        price_paise: number;
        price_unit: 'per_group';
        currency: 'INR';
      }>;
    }>;
    hotels: Array<{
      provider: string;
      hotelId: string;
      name: string;
      starRating: string;
      address: string;
      description: string;
      phone_number: string;
      price: number;
      currency: string;
      amenities: string[];
      thumbnail: string | null;
      images?: string[];
      gallery?: string[];
      roomType?: string;
      checkInDate?: string;
      checkOutDate?: string;
    }>;
    transfers: Array<{
      provider: string;
      name: string;
      vehicle?: string;
      pickupDate?: string;
      pickupTime?: string;
      pickupFromAirport?: boolean;
      airportCode?: string;
      airportName?: string;
      hotelCode?: string;
      hotelName?: string;
      meta?: {
        basis?: ServiceBasis;
        rawText?: string;
      };
    }>;
    points: string[];
  }>;
  exclusions: string[];
  visaDetails: Array<{
    type: string;
    price: number;
    validity?: string;
    documents?: string[];
    timeRequired?: string;
    currency?: string;
  }>;
  insuranceDetails: Array<{
    name: string;
    price: number;
    currency?: string;
    description?: string;
  }>;
  mealPlan?: string | null;
  selectedHotelRoomCount?: number | null;
  selectedCab?: {
    cabType?: string;
    maxOccupancy?: number;
    transferType?: string;
  } | null;
};

export type ExtractForQuotationResponse = {
  requestId: string;
  status: ExtractionStatus;
  parserVersion: string;
  extractedAt: string;
  source: {
    rawText: string;
    textHash: string;
  };
  warnings: ExtractionWarning[];
  parsed: {
    guestName?: string | null;
    adults?: number | null;
    children?: number | null;
    infants?: number | null;
    tripId?: string | null;
    bookedBy?: string | null;
    preparedBy?: string | null;
    periodStart?: string | null;
    periodEnd?: string | null;
    contacts: {
      emergencyContact?: string | null;
      airportRepresentative?: string | null;
      guestNumber?: string | null;
    };
    clubBenefits: Array<{
      title: string;
      description: string;
      rawText: string;
    }>;
    generalNotes: string[];
    stays: Array<{
      hotelName: string;
      roomType?: string | null;
      checkInDate?: string | null;
      checkOutDate?: string | null;
      rawText: string;
    }>;
    flights: Array<{
      kind: 'arrival' | 'departure' | 'other';
      flightNumber?: string | null;
      airline?: string | null;
      airportCode?: string | null;
      departureDate?: string | null;
      departureTime?: string | null;
      rawText: string;
    }>;
    days: Array<{
      dayNumber?: number | null;
      date?: string | null;
      accommodation?: string[];
      hotelTransition?: {
        fromHotel: string;
        toHotel: string;
      } | null;
      services: Array<{
        kind: 'transfer' | 'activity' | 'excursion' | 'sightseeing' | 'note';
        title: string;
        description: string;
        basis: ServiceBasis;
        startTime?: string | null;
        finishTime?: string | null;
        pickupTime?: string | null;
        accommodationText?: string | null;
        meals?: string[];
        rawText: string;
        confidence?: number | null;
      }>;
      rawText: string;
    }>;
    terms: string[];
    tips: string[];
  };
  builderSeed: QuotationItineraryBuilderSeed;
};

type ExtractForQuotationRequest = {
  rawText: string;
  queryId?: number | null;
  customerHint?: {
    name?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  };
  tripStartDateHint?: string | null;
};

export const extractQuotationItinerary = async (
  payload: ExtractForQuotationRequest
): Promise<ExtractForQuotationResponse> => {
  const aiBaseUrl = getAiBaseUrl();
  if (!aiBaseUrl) {
    throw new Error(
      [
        'Missing REACT_APP_AI_BACKEND_BASE_URL.',
        'Create query-app/.env and restart the dev server.',
        'Example: REACT_APP_AI_BACKEND_BASE_URL=http://localhost:3004',
      ].join(' ')
    );
  }

  const endpoint = `${aiBaseUrl.replace(/\/$/, '')}/api/itineraries/extract-for-quotation`;
  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...getApiHeaders('json'),
        'X-Client-Platform': 'query-app',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown network error';
    throw new Error(
      `Unable to reach quotation itinerary extraction API at ${endpoint}. ` +
      `Check that ai-backend is running and that CORS allows the query-app origin. ` +
      `Underlying error: ${message}`
    );
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to extract itinerary');
  }

  return response.json();
};
